import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StackProvider, StackTheme, StackHandler } from '@stackframe/react';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { stackClientApp } from './stack/client';
import Layout from './components/Layout';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import MyFamilyPage from './pages/MyFamilyPage';
import SmartDaddyPage from './pages/SmartDaddyPage';
import SettingsPage from './pages/SettingsPage';
import StatisticsPage from './pages/StatisticsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import AdminPage from './pages/AdminPage';
import './App.css';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!user) {
    return <Navigate to="/handler/sign-in" replace />;
  }
  
  return children;
};

// Stack Auth Handler Routes
function HandlerRoutes() {
  const location = useLocation();
  
  return (
    <StackHandler 
      app={stackClientApp} 
      location={location.pathname} 
      fullPage 
    />
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Stack Auth Handler Routes */}
      <Route path="/handler/*" element={<HandlerRoutes />} />
      
      {/* Landing/Auth Page */}
      <Route path="/" element={<AuthPage />} />
      <Route path="/auth" element={<AuthPage />} />
      
      {/* Protected App Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/family" element={
        <ProtectedRoute>
          <Layout>
            <MyFamilyPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/smartdaddy" element={
        <ProtectedRoute>
          <Layout>
            <SmartDaddyPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/statistics" element={
        <ProtectedRoute>
          <Layout>
            <StatisticsPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Redirects for old routes */}
      <Route path="/activities" element={<Navigate to="/family?tab=activities" replace />} />
      <Route path="/events" element={<Navigate to="/family?tab=events" replace />} />
      <Route path="/import" element={<Navigate to="/family?tab=import" replace />} />
      <Route path="/summaries" element={<Navigate to="/smartdaddy" replace />} />
      <Route path="/assistant" element={<Navigate to="/smartdaddy" replace />} />
      
      {/* Public Pages */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <BrowserRouter>
        <StackProvider app={stackClientApp}>
          <StackTheme>
            <AuthProvider>
              <ScrollToTop />
              <AppRoutes />
              <Toaster position="top-right" richColors closeButton />
            </AuthProvider>
          </StackTheme>
        </StackProvider>
      </BrowserRouter>
    </Suspense>
  );
}

export default App;
